/*
  Keeps ambient section videos (the muted, looping background clips) playing on
  their own. Two things get in the way of that:

    1. Browsers only allow autoplay for video that is both muted and inline, and
       Safari in particular needs the `playsinline` attribute present.
    2. Dawn's pauseAllMedia() stops every <video> on the page whenever a modal or
       drawer opens, and nothing ever resumes them — so opening the cart once
       left the homepage videos frozen until the shopper clicked play.

  global.js now skips [data-ambient-video] in pauseAllMedia(); this file covers
  the autoplay rules and the remaining ways a video can end up stopped.
*/
(() => {
  const VIDEO_SELECTOR = '[data-ambient-video] video';

  // A pause landing this soon after a click or keypress on the video came from
  // the shopper using the controls, not from another script.
  const USER_PAUSE_WINDOW = 1000;

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              entry.target.dataset.ambientVisible = entry.isIntersecting ? 'true' : 'false';
              if (entry.isIntersecting) {
                play(entry.target);
              } else {
                entry.target.pause();
              }
            });
          },
          { rootMargin: '200px 0px', threshold: 0.01 }
        )
      : null;

  function play(video) {
    if (video.dataset.ambientUserPaused === 'true') return;
    if (video.dataset.ambientVisible === 'false') return;

    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  function setUp(video) {
    if (video.dataset.ambientReady === 'true') return;
    video.dataset.ambientReady = 'true';

    // Set the properties *and* the attributes — Safari checks the attributes.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    let lastInteraction = 0;
    const noteInteraction = () => {
      lastInteraction = Date.now();
    };
    video.addEventListener('pointerdown', noteInteraction);
    video.addEventListener('keydown', noteInteraction);

    video.addEventListener('pause', () => {
      video.dataset.ambientUserPaused = Date.now() - lastInteraction < USER_PAUSE_WINDOW ? 'true' : 'false';
    });
    video.addEventListener('play', () => {
      video.dataset.ambientUserPaused = 'false';
    });

    // Safari can fire 'ended' instead of looping when the file has no audio track.
    video.addEventListener('ended', () => play(video));

    if (observer) {
      observer.observe(video);
    } else {
      play(video);
    }
  }

  function scan(root) {
    (root || document).querySelectorAll(VIDEO_SELECTOR).forEach(setUp);
  }

  function resumeAll() {
    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      if (video.paused) play(video);
    });
  }

  // Coming back to a backgrounded tab, and closing any Dawn modal, both leave
  // videos paused.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resumeAll();
  });
  document.body.addEventListener('modalClosed', resumeAll);

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  scan();
})();
